import { useEffect, useRef } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import {
  BookMarkedIcon,
  BookOpenIcon,
  BookPlusIcon,
  DatabaseIcon,
  FileDownIcon,
  FilePenLineIcon,
  FilePlus2Icon,
  FileSearch2Icon,
  FlaskConicalIcon,
  FolderOpenIcon,
  InfoIcon,
  LayoutGridIcon,
  LinkIcon,
  ExternalLinkIcon,
  PencilIcon,
  SearchIcon,
  Trash2Icon,
  BrainIcon,
  CheckCircle2Icon,
  PowerIcon,
  PowerOffIcon,
  SendIcon,
  SettingsIcon,
  LightbulbIcon,
  GlobeIcon,
  MapIcon,
  PackageIcon,
  ZapIcon,
} from 'lucide-react';


/**
 * ToolCard — disabled. Tool calls now render via ToolCallsBlock as compact rows
 * in a collapsible reasoning block. This stub returns null to satisfy the
 * makeAssistantToolUI registrations that reference it.
 */
function ToolCard(_props: {
  icon: React.ElementType;
  label: string;
  detail?: string;
  result?: unknown;
  status: { type: string };
  className?: string;
}) {
  return null;
}

// ===========================================================================
// Filesystem tools
// ===========================================================================

export const ListDirectoryToolUI = makeAssistantToolUI<{ path: string }, unknown>({
  toolName: 'list_directory',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<string[]>(rawResult);
    const count = result?.length ?? 0;
    const detail =
      status.type === 'running'
        ? 'Browsing files...'
        : count > 0
          ? `${count} item${count !== 1 ? 's' : ''} found`
          : 'No items found';

    return <ToolCard icon={FolderOpenIcon} label="Browse Files" detail={detail} status={status} />;
  },
});

export const ReadFileToolUI = makeAssistantToolUI<
  { file_path: string; offset?: number; limit?: number },
  unknown
>({
  toolName: 'read_file',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Reading document...' : 'Document loaded';

    return (
      <ToolCard icon={FileSearch2Icon} label="Read Document" detail={detail} status={status} />
    );
  },
});

export const WriteFileToolUI = makeAssistantToolUI<{ file_path: string; content: string }, unknown>(
  {
    toolName: 'write_file',
    render: ({ status }) => {
      const detail = status.type === 'running' ? 'Saving document...' : 'Document saved';

      return (
        <ToolCard icon={FilePlus2Icon} label="Save Document" detail={detail} status={status} />
      );
    },
  },
);

export const EditFileToolUI = makeAssistantToolUI<
  { file_path: string; old_string: string; new_string: string; replace_all?: boolean },
  unknown
>({
  toolName: 'edit_file',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Updating document...' : 'Document updated';

    return (
      <ToolCard icon={FilePenLineIcon} label="Update Document" detail={detail} status={status} />
    );
  },
});

export const DeleteFileToolUI = makeAssistantToolUI<{ file_path: string }, unknown>({
  toolName: 'delete_file',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Deleting...' : 'File deleted';

    return <ToolCard icon={Trash2Icon} label="Delete File" detail={detail} status={status} />;
  },
});

// ===========================================================================
// Knowledge & context tools
// ===========================================================================

export const LoadSkillToolUI = makeAssistantToolUI<{ name?: string }, unknown>({
  toolName: 'load_skill',
  render: ({ args, status }) => {
    const name = args?.name;
    const detail =
      status.type === 'running'
        ? name
          ? `Loading "${name}"...`
          : 'Loading...'
        : name
          ? `"${name}" knowledge loaded`
          : 'Knowledge loaded';

    return <ToolCard icon={BrainIcon} label="Load Knowledge" detail={detail} status={status} />;
  },
});

export const GetContextSourceToolUI = makeAssistantToolUI<Record<string, never>, unknown>({
  toolName: 'get_context_source',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Loading context...' : 'Context loaded';
    return <ToolCard icon={InfoIcon} label="Get Context" detail={detail} status={status} />;
  },
});

// ===========================================================================
// Opportunities
// ===========================================================================

export const ListOpportunitiesToolUI = makeAssistantToolUI<
  { shop_integration_id: number; limit?: number },
  unknown
>({
  toolName: 'list_opportunities',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ count?: number }>(rawResult);
    const count = result?.count;
    const detail =
      status.type === 'running'
        ? 'Loading opportunities...'
        : count != null
          ? `${count} opportunit${count !== 1 ? 'ies' : 'y'} found`
          : 'Opportunities loaded';
    return (
      <ToolCard icon={LightbulbIcon} label="List Opportunities" detail={detail} status={status} />
    );
  },
});

export const GetOpportunityToolUI = makeAssistantToolUI<
  { shop_integration_id: number; opportunity_id: number },
  unknown
>({
  toolName: 'get_opportunity',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ insight?: string }>(rawResult);
    const insight = result?.insight;
    const detail =
      status.type === 'running' ? 'Loading opportunity...' : (insight ?? 'Opportunity loaded');
    return (
      <ToolCard icon={LightbulbIcon} label="Get Opportunity" detail={detail} status={status} />
    );
  },
});

export const ProcessOpportunityToolUI = makeAssistantToolUI<
  { shop_integration_id: number; opportunity_id: number; payload: string | object },
  unknown
>({
  toolName: 'process_opportunity',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }, [status.type]);

    const result = parseResult<{ action?: string }>(rawResult);
    const action = result?.action;
    const detail =
      status.type === 'running'
        ? 'Processing opportunity...'
        : action === 'APPROVE'
          ? 'Opportunity approved'
          : action === 'DISMISS'
            ? 'Opportunity dismissed'
            : action === 'RESOLVE_CONFLICT'
              ? 'Conflict resolved'
              : 'Opportunity processed';
    return (
      <ToolCard
        icon={LightbulbIcon}
        label="Process Opportunity"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

// ===========================================================================
// Data & query tools
// ===========================================================================

const renderQuery = ({ result: rawResult, status }: { result?: unknown; status: { type: string } }) => {
  const result = parseResult<{ count?: number }>(rawResult);
  const count = result?.count ?? 0;
  const detail =
    status.type === 'running'
      ? 'Analyzing data...'
      : `Found ${count} result${count !== 1 ? 's' : ''}`;
  return <ToolCard icon={DatabaseIcon} label="Analyze Data" detail={detail} status={status} />;
};

export const QueryToolUI = makeAssistantToolUI({ toolName: 'query', render: renderQuery });
export const ExecuteQueryToolUI = makeAssistantToolUI({
  toolName: 'execute_query',
  render: renderQuery,
});

export const ExportDataToolUI = makeAssistantToolUI<{ sql_query: string }, unknown>({
  toolName: 'export_data',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Preparing your export...' : 'Export ready';
    return <ToolCard icon={FileDownIcon} label="Export Data" detail={detail} status={status} />;
  },
});

export const GetTableMetadataToolUI = makeAssistantToolUI<
  { table_name: string; request: string; column_names?: string[] | null },
  unknown
>({
  toolName: 'get_table_metadata',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Looking up data...' : 'Data loaded';
    return <ToolCard icon={SearchIcon} label="Look Up Data" detail={detail} status={status} />;
  },
});

// ===========================================================================

export const ListGuidancesToolUI = makeAssistantToolUI<
  { include_deleted?: boolean; max_guidances?: number; base_path?: string },
  unknown
>({
  toolName: 'list_guidances',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ count?: number }>(rawResult);
    const count = result?.count;
    const detail =
      status.type === 'running'
        ? 'Loading guidances...'
        : count != null
          ? `${count} guidance${count !== 1 ? 's' : ''} loaded`
          : 'Guidances loaded';
    return (
      <ToolCard icon={BookMarkedIcon} label="List Guidances" detail={detail} status={status} />
    );
  },
});

export const GetGuidanceToolUI = makeAssistantToolUI<{ guidance_id?: string }, unknown>({
  toolName: 'get_guidance',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ title?: string }>(rawResult);
    const title = result?.title;
    const detail = status.type === 'running' ? 'Reading guidance...' : (title ?? 'Guidance loaded');
    return <ToolCard icon={BookOpenIcon} label="Read Guidance" detail={detail} status={status} />;
  },
});

export const UpdateDraftGuidanceToolUI = makeAssistantToolUI<{ guidance_id?: string }, unknown>({
  toolName: 'update_draft_guidance',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }, [status.type]);

    const result = parseResult<{ title?: string }>(rawResult);
    const title = result?.title;
    const detail =
      status.type === 'running'
        ? 'Updating guidance...'
        : title
          ? `"${title}" updated`
          : 'Guidance updated';
    return (
      <ToolCard
        icon={PencilIcon}
        label="Update Guidance"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const CreateDraftGuidanceToolUI = makeAssistantToolUI<{ title?: string }, unknown>({
  toolName: 'create_draft_guidance',
  render: function Render({ args, result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }, [status.type]);

    const title = args?.title;
    const detail =
      status.type === 'running'
        ? 'Creating guidance...'
        : title
          ? `"${title}" created`
          : 'Guidance created';
    return (
      <ToolCard
        icon={BookPlusIcon}
        label="Create Draft Guidance"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const PublishGuidanceToolUI = makeAssistantToolUI<{ article_id?: string }, unknown>({
  toolName: 'publish_guidance',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }, [status.type]);

    const result = parseResult<{ title?: string }>(rawResult);
    const title = result?.title;
    const detail =
      status.type === 'running'
        ? 'Publishing guidance...'
        : title
          ? `"${title}" published`
          : 'Guidance published';
    return (
      <ToolCard
        icon={SendIcon}
        label="Publish Guidance"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const ListGuidanceTemplatesToolUI = makeAssistantToolUI<Record<string, never>, unknown>({
  toolName: 'list_guidance_templates',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<unknown[]>(rawResult);
    const count = result?.length;
    const detail =
      status.type === 'running'
        ? 'Loading templates...'
        : count != null
          ? `${count} template${count !== 1 ? 's' : ''} found`
          : 'Templates loaded';
    return (
      <ToolCard icon={LayoutGridIcon} label="Guidance Templates" detail={detail} status={status} />
    );
  },
});

// ===========================================================================
// Skills (intent-linked articles)
// ===========================================================================

export const ListSkillsToolUI = makeAssistantToolUI<{ shop_name: string }, unknown>({
  toolName: 'list_skills',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ count?: number }>(rawResult);
    const count = result?.count;
    const detail =
      status.type === 'running'
        ? 'Loading skills...'
        : count != null
          ? `${count} skill${count !== 1 ? 's' : ''} loaded`
          : 'Skills loaded';
    return <ToolCard icon={BookMarkedIcon} label="List Skills" detail={detail} status={status} />;
  },
});

export const GetSkillToolUI = makeAssistantToolUI<
  { help_center_id: number; article_id: number },
  unknown
>({
  toolName: 'get_skill',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ title?: string }>(rawResult);
    const title = result?.title;
    const detail = status.type === 'running' ? 'Reading skill...' : (title ?? 'Skill loaded');
    return <ToolCard icon={BookOpenIcon} label="Read Skill" detail={detail} status={status} />;
  },
});

export const CreateDraftSkillToolUI = makeAssistantToolUI<
  { title?: string; intents?: string[] },
  unknown
>({
  toolName: 'create_draft_skill',
  render: function Render({ args, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage(
        { type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['help-centers'] },
        '*',
      );
    }, [status.type]);

    const title = args?.title;
    const detail =
      status.type === 'running'
        ? 'Creating skill...'
        : title
          ? `"${title}" created`
          : 'Skill created';
    return <ToolCard icon={BookPlusIcon} label="Create Draft Skill" detail={detail} status={status} />;
  },
});

export const UpdateDraftSkillToolUI = makeAssistantToolUI<
  { article_id?: number; intents?: string[] },
  unknown
>({
  toolName: 'update_draft_skill',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage(
        { type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['help-centers'] },
        '*',
      );
    }, [status.type]);

    const result = parseResult<{ title?: string }>(rawResult);
    const title = result?.title;
    const detail =
      status.type === 'running'
        ? 'Updating skill...'
        : title
          ? `"${title}" updated`
          : 'Skill updated';
    return <ToolCard icon={PencilIcon} label="Update Skill" detail={detail} status={status} />;
  },
});

export const PublishSkillToolUI = makeAssistantToolUI<
  { help_center_id?: number; article_id?: number },
  unknown
>({
  toolName: 'publish_skill',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage(
        { type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['help-centers'] },
        '*',
      );
    }, [status.type]);

    const result = parseResult<{ title?: string }>(rawResult);
    const title = result?.title;
    const detail =
      status.type === 'running'
        ? 'Publishing skill...'
        : title
          ? `"${title}" published`
          : 'Skill published';
    return <ToolCard icon={SendIcon} label="Publish Skill" detail={detail} status={status} />;
  },
});

export const ListIntentsToolUI = makeAssistantToolUI<{ shop_name: string }, unknown>({
  toolName: 'list_intents',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ count?: number }>(rawResult);
    const count = result?.count;
    const detail =
      status.type === 'running'
        ? 'Loading intents...'
        : count != null
          ? `${count} intent${count !== 1 ? 's' : ''} loaded`
          : 'Intents loaded';
    return <ToolCard icon={BookMarkedIcon} label="List Intents" detail={detail} status={status} />;
  },
});

// ===========================================================================
// AI Agent settings
// ===========================================================================

export const GetAgentConfigurationToolUI = makeAssistantToolUI<{ store_name: string }, unknown>({
  toolName: 'get_agent_configuration',
  render: ({ status }) => {
    const detail = status.type === 'running' ? 'Loading configuration...' : 'Configuration loaded';
    return (
      <ToolCard icon={SettingsIcon} label="Agent Configuration" detail={detail} status={status} />
    );
  },
});

export const UpdateToneOfVoiceToolUI = makeAssistantToolUI<
  { shop_name: string; toneOfVoice: string; customToneOfVoiceGuidance?: string | null },
  unknown
>({
  toolName: 'update_tone_of_voice',
  render: function Render({ args, result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE' }, '*');
    }, [status.type]);

    const tone = args?.toneOfVoice;
    const detail =
      status.type === 'running'
        ? 'Updating tone of voice...'
        : tone
          ? `Tone set to "${tone}"`
          : 'Tone of voice updated';
    return (
      <ToolCard
        icon={SettingsIcon}
        label="Update Tone of Voice"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const UpdateExcludedHandoverTopicsToolUI = makeAssistantToolUI<
  { shop_name: string; excludedTopics: string[] },
  unknown
>({
  toolName: 'update_excluded_handover_topics',
  render: function Render({ args, result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE' }, '*');
    }, [status.type]);

    const topics = args?.excludedTopics;
    const detail =
      status.type === 'running'
        ? 'Updating handover topics...'
        : topics?.length
          ? `Excluded: ${topics.join(', ')}`
          : 'Handover topics updated';
    return (
      <ToolCard
        icon={SettingsIcon}
        label="Update Handover Topics"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

// ===========================================================================
// Merchant website tools
// ===========================================================================

export const GetMerchantSitemapToolUI = makeAssistantToolUI<{ shop_name: string }, unknown>({
  toolName: 'get_merchant_sitemap',
  render: ({ status }) => {
    const detail =
      status.type === 'running' ? 'Discovering pages...' : 'Sitemap loaded';
    return <ToolCard icon={MapIcon} label="Merchant Sitemap" detail={detail} status={status} />;
  },
});

export const ReadMerchantWebsiteToolUI = makeAssistantToolUI<
  { shop_name: string; path?: string },
  unknown
>({
  toolName: 'read_merchant_website',
  render: ({ args, status }) => {
    const page = args?.path || '/';
    const detail =
      status.type === 'running' ? `Reading ${page}...` : `Read ${page}`;
    return (
      <ToolCard icon={GlobeIcon} label="Read Website" detail={detail} status={status} />
    );
  },
});

export const GetProductCatalogToolUI = makeAssistantToolUI<
  { shop_name: string; collection_handle?: string },
  unknown
>({
  toolName: 'get_product_catalog',
  render: ({ args, status }) => {
    const scope = args?.collection_handle || 'full catalog';
    const detail =
      status.type === 'running' ? `Fetching ${scope}...` : `${scope} loaded`;
    return (
      <ToolCard icon={PackageIcon} label="Product Catalog" detail={detail} status={status} />
    );
  },
});

// ===========================================================================
// AI Agent actions tools
// ===========================================================================

export const ListSupportActionsToolUI = makeAssistantToolUI<{ store_name: string }, unknown>({
  toolName: 'list_support_actions',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<unknown[]>(rawResult);
    const count = result?.length;
    const detail =
      status.type === 'running'
        ? 'Loading actions...'
        : count != null
          ? `${count} action${count !== 1 ? 's' : ''} found`
          : 'Actions loaded';
    return <ToolCard icon={ZapIcon} label="List Actions" detail={detail} status={status} />;
  },
});

export const GetSupportActionToolUI = makeAssistantToolUI<{ action_id: string }, unknown>({
  toolName: 'get_support_action',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail = status.type === 'running' ? 'Loading action...' : (name ?? 'Action loaded');
    return <ToolCard icon={ZapIcon} label="Get Action" detail={detail} status={status} />;
  },
});

export const CreateSupportActionToolUI = makeAssistantToolUI<
  { payload: string | object; store_name: string },
  unknown
>({
  toolName: 'create_support_action',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['store-workflow-configuration', 'workflow-configuration'] }, '*');
    }, [status.type]);

    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail =
      status.type === 'running'
        ? 'Creating action...'
        : name
          ? `"${name}" created`
          : 'Action created';
    return (
      <ToolCard
        icon={ZapIcon}
        label="Create Action"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const EnableSupportActionToolUI = makeAssistantToolUI<
  { action_id: string; store_name: string },
  unknown
>({
  toolName: 'enable_support_action',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['store-workflow-configuration', 'workflow-configuration'] }, '*');
    }, [status.type]);

    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail =
      status.type === 'running'
        ? 'Enabling action...'
        : name
          ? `"${name}" enabled`
          : 'Action enabled';
    return (
      <ToolCard
        icon={PowerIcon}
        label="Enable Action"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const DisableSupportActionToolUI = makeAssistantToolUI<
  { action_id: string; store_name: string },
  unknown
>({
  toolName: 'disable_support_action',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['store-workflow-configuration', 'workflow-configuration'] }, '*');
    }, [status.type]);

    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail =
      status.type === 'running'
        ? 'Disabling action...'
        : name
          ? `"${name}" disabled`
          : 'Action disabled';
    return (
      <ToolCard
        icon={PowerOffIcon}
        label="Disable Action"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const UpdateSupportActionToolUI = makeAssistantToolUI<
  { action_id: string; changes: string | object; store_name: string },
  unknown
>({
  toolName: 'update_support_action',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['store-workflow-configuration', 'workflow-configuration'] }, '*');
    }, [status.type]);

    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail =
      status.type === 'running'
        ? 'Updating action...'
        : name
          ? `"${name}" updated`
          : 'Action updated';
    return (
      <ToolCard
        icon={PencilIcon}
        label="Update Action"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const ConvertToAdvancedViewToolUI = makeAssistantToolUI<
  { action_id: string; store_name: string },
  unknown
>({
  toolName: 'convert_to_advanced_view',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_PAGE', queryKeys: ['store-workflow-configuration', 'workflow-configuration'] }, '*');
    }, [status.type]);

    const detail =
      status.type === 'running' ? 'Converting to Advanced View...' : 'Converted to Advanced View';
    return (
      <ToolCard
        icon={SettingsIcon}
        label="Advanced View"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

// ===========================================================================
// AI Agent action templates
// ===========================================================================

export const ListActionTemplatesToolUI = makeAssistantToolUI<Record<string, never>, unknown>({
  toolName: 'list_action_templates',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<unknown[]>(rawResult);
    const count = result?.length;
    const detail =
      status.type === 'running'
        ? 'Loading templates...'
        : count != null
          ? `${count} template${count !== 1 ? 's' : ''} found`
          : 'Templates loaded';
    return (
      <ToolCard icon={LayoutGridIcon} label="List Templates" detail={detail} status={status} />
    );
  },
});

export const GetActionTemplateToolUI = makeAssistantToolUI<{ template_id: string }, unknown>({
  toolName: 'get_action_template',
  render: ({ result: rawResult, status }) => {
    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail =
      status.type === 'running' ? 'Loading template...' : (name ?? 'Template loaded');
    return (
      <ToolCard
        icon={LayoutGridIcon}
        label="Get Template"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

export const CreateActionFromTemplateToolUI = makeAssistantToolUI<
  { template_id: string; store_name: string; selected_apps?: string[] },
  unknown
>({
  toolName: 'create_action_from_template',
  render: function Render({ result: rawResult, status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }, [status.type]);

    const result = parseResult<{ name?: string }>(rawResult);
    const name = result?.name;
    const detail =
      status.type === 'running'
        ? 'Deploying template...'
        : name
          ? `"${name}" deployed`
          : 'Template deployed';
    return (
      <ToolCard
        icon={ZapIcon}
        label="Deploy Template"
        detail={detail}
        result={rawResult}
        status={status}
      />
    );
  },
});

// ===========================================================================
// Navigation
// ===========================================================================

export const NavigateUserToolUI = makeAssistantToolUI<{ path: string }, string>({
  toolName: 'navigate_user',
  render: function Render({ args, status }) {
    const hasFired = useRef(false);
    const wasStreaming = useRef(false);

    // Track whether this tool call was ever in 'running' state (live stream).
    // Historical tool calls from reloaded threads mount as 'complete' immediately.
    if (status.type === 'running') {
      wasStreaming.current = true;
    }

    useEffect(() => {
      if (status.type !== 'complete' || !args?.path || hasFired.current || !wasStreaming.current)
        return;
      hasFired.current = true;

      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
      setTimeout(() => {
        window.parent.postMessage({ type: 'GAIA_EMBED_NAVIGATE', url: args.path }, '*');
      }, 100);
    }, [status.type, args?.path]);

    const detail = status.type === 'running' ? 'Navigating...' : 'Page opened';

    return <ToolCard icon={ExternalLinkIcon} label="Navigate" detail={detail} status={status} />;
  },
});

// ===========================================================================
// Cortex: Conversation tools
// ===========================================================================

export const GenerateFinalAnswerToolUI = makeAssistantToolUI({
  toolName: 'generate_final_answer',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Preparing answer...' : 'Answer ready';
    return (
      <ToolCard icon={CheckCircle2Icon} label="Final Answer" detail={detail} status={status} />
    );
  },
});

export const AskClarifyingQuestionsToolUI = makeAssistantToolUI({
  toolName: 'ask_clarifying_questions',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Preparing questions...' : 'Questions sent';
    return (
      <ToolCard icon={InfoIcon} label="Clarifying Questions" detail={detail} status={status} />
    );
  },
});

// ===========================================================================
// Cortex: Web tools
// ===========================================================================

export const WebSearchToolUI = makeAssistantToolUI({
  toolName: 'web_search',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Searching the web...' : 'Search complete';
    return <ToolCard icon={SearchIcon} label="Web Search" detail={detail} status={status} />;
  },
});

export const WebFetchToolUI = makeAssistantToolUI({
  toolName: 'web_fetch',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Fetching content...' : 'Content loaded';
    return <ToolCard icon={ExternalLinkIcon} label="Fetch URL" detail={detail} status={status} />;
  },
});

// ===========================================================================
// Cortex: Utility tools
// ===========================================================================

export const GenerateSignedGcsLinkToolUI = makeAssistantToolUI({
  toolName: 'generate_signed_gcs_link',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Generating link...' : 'Link ready';
    return <ToolCard icon={LinkIcon} label="Generate Link" detail={detail} status={status} />;
  },
});

export const ProvideSqlToolUI = makeAssistantToolUI({
  toolName: 'provide_sql',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Parsing SQL...' : 'SQL ready';
    return <ToolCard icon={DatabaseIcon} label="Provide SQL" detail={detail} status={status} />;
  },
});

export const SetThinkingModeToolUI = makeAssistantToolUI({
  toolName: 'set_thinking_mode',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Switching mode...' : 'Mode updated';
    return <ToolCard icon={BrainIcon} label="Thinking Mode" detail={detail} status={status} />;
  },
});

export const ProvideFeedbackToolUI = makeAssistantToolUI({
  toolName: 'provide_feedback',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Recording feedback...' : 'Feedback recorded';
    return <ToolCard icon={LightbulbIcon} label="Provide Feedback" detail={detail} status={status} />;
  },
});

// ===========================================================================
// Cortex: Slack tools
// ===========================================================================

export const SendSlackMessageToolUI = makeAssistantToolUI({
  toolName: 'send_slack_message',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Sending message...' : 'Message sent';
    return <ToolCard icon={SendIcon} label="Send Slack Message" detail={detail} status={status} />;
  },
});

export const ReadCurrentThreadToolUI = makeAssistantToolUI({
  toolName: 'read_current_thread',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Reading thread...' : 'Thread loaded';
    return <ToolCard icon={FileSearch2Icon} label="Read Thread" detail={detail} status={status} />;
  },
});

export const ReadSlackChannelToolUI = makeAssistantToolUI({
  toolName: 'read_slack_channel',
  render: ({ status }: { status: { type: string } }) => {
    const detail = status.type === 'running' ? 'Reading channel...' : 'Channel loaded';
    return (
      <ToolCard icon={FileSearch2Icon} label="Read Channel" detail={detail} status={status} />
    );
  },
});

export const SearchSlackUserToolUI = makeAssistantToolUI({
  toolName: 'search_slack_user',
  render: ({ result: rawResult, status }: { result?: unknown; status: { type: string } }) => {
    const result = parseResult<unknown[]>(rawResult);
    const count = result?.length;
    const detail =
      status.type === 'running'
        ? 'Searching users...'
        : count != null
          ? `${count} user${count !== 1 ? 's' : ''} found`
          : 'Search complete';
    return (
      <ToolCard icon={SearchIcon} label="Search Slack User" detail={detail} status={status} />
    );
  },
});

export const SearchSlackChannelToolUI = makeAssistantToolUI({
  toolName: 'search_slack_channel',
  render: ({ result: rawResult, status }: { result?: unknown; status: { type: string } }) => {
    const result = parseResult<unknown[]>(rawResult);
    const count = result?.length;
    const detail =
      status.type === 'running'
        ? 'Searching channels...'
        : count != null
          ? `${count} channel${count !== 1 ? 's' : ''} found`
          : 'Search complete';
    return (
      <ToolCard icon={SearchIcon} label="Search Slack Channel" detail={detail} status={status} />
    );
  },
});

// ===========================================================================
// AI Agent preview / test mode
// ===========================================================================

export const PreviewAiAgentToolUI = makeAssistantToolUI<
  { customer_message: string; channel?: string },
  unknown
>({
  toolName: 'preview_ai_agent',
  render: ({ args, result: rawResult, status }) => {
    const result = parseResult<{
      ai_reply?: string;
      outcome?: string;
      customer_message?: string;
      reasoning?: {
        reasoning?: Array<{ targetType?: string; value?: string }>;
        resources?: unknown[];
      };
    }>(rawResult);

    const isRunning = status.type === 'running';
    const detail = isRunning ? 'Testing AI Agent...' : 'Preview complete';

    return (
      <div className="my-2.5">
        <ToolCard
          icon={FlaskConicalIcon}
          label="Preview AI Agent"
          detail={detail}
          status={status}
        />
        {!isRunning && result?.ai_reply && (
          <div className="ml-11 -mt-1 space-y-2 rounded-b-lg border border-t-0 bg-muted/20 px-3.5 py-2.5 text-sm">
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">Customer</div>
              <p className="text-foreground">{result.customer_message ?? args.customer_message}</p>
            </div>
            <div className="h-px bg-border" />
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">AI Agent</div>
              <div
                className="prose prose-sm max-w-none text-foreground [&_p]:my-1"
                dangerouslySetInnerHTML={{ __html: result.ai_reply }}
              />
            </div>
            {result.reasoning?.reasoning && result.reasoning.reasoning.length > 0 && (
              <>
                <div className="h-px bg-border" />
                <details className="group">
                  <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
                    <BrainIcon className="size-3" />
                    Reasoning ({result.reasoning.reasoning.length} steps)
                  </summary>
                  <div className="mt-2 space-y-1.5 pl-4 text-xs text-muted-foreground">
                    {result.reasoning.reasoning.map((r, i) => (
                      <div key={i}>
                        <span className="font-medium text-foreground">{r.targetType}:</span>{' '}
                        {r.value}
                      </div>
                    ))}
                  </div>
                </details>
              </>
            )}
          </div>
        )}
      </div>
    );
  },
});

export const PreviewToneOfVoiceToolUI = makeAssistantToolUI<
  { customer_message: string; tone_of_voice: string },
  unknown
>({
  toolName: 'preview_tone_of_voice',
  render: ({ args, result: rawResult, status }) => {
    const result = parseResult<{
      customer_message?: string;
      tone_of_voice?: string;
      ai_answer?: string;
    }>(rawResult);

    const isRunning = status.type === 'running';
    const detail = isRunning ? 'Generating preview...' : 'Preview complete';

    return (
      <div className="my-2.5">
        <ToolCard
          icon={FlaskConicalIcon}
          label="Preview Tone of Voice"
          detail={detail}
          status={status}
        />
        {!isRunning && result?.ai_answer && (
          <div className="ml-11 -mt-1 space-y-2 rounded-b-lg border border-t-0 bg-muted/20 px-3.5 py-2.5 text-sm">
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">Customer</div>
              <p className="text-foreground">{result.customer_message ?? args.customer_message}</p>
            </div>
            <div className="h-px bg-border" />
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">AI Agent (with custom tone)</div>
              <div
                className="prose prose-sm max-w-none text-foreground [&_p]:my-1"
                dangerouslySetInnerHTML={{ __html: result.ai_answer }}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse a tool result that may be a JSON string or already an object. */
// ===========================================================================
// Plan — renders nothing (plan is shown in sticky footer via PlanContext)
// ===========================================================================

export const WritePlanToolUI = makeAssistantToolUI({
  toolName: 'write_plan',
  render: () => null,
});

// ===========================================================================
// Tasks — execute_task triggers parent cache refresh
// ===========================================================================

export const ExecuteTaskToolUI = makeAssistantToolUI<{ task_id?: string }, unknown>({
  toolName: 'execute_task',
  render: function Render({ status }) {
    const hasFired = useRef(false);
    useEffect(() => {
      if (status.type !== 'complete' || hasFired.current) return;
      hasFired.current = true;
      window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    }, [status.type]);
    // Rendered by ToolCallsBlock — return null here
    return null;
  },
});

function parseResult<T>(result: unknown): T | undefined {
  if (result == null) return undefined;
  if (typeof result === 'string') {
    try {
      return JSON.parse(result) as T;
    } catch {
      return undefined;
    }
  }
  return result as T;
}
